import { LightningBoltIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import { FaSlack } from 'react-icons/fa6';

import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand, DemoMessage, DemoStep } from './types';
import { AgentBubble, AgentReply, VisitorBubble } from '../ChatBubbles';
import { ArrowGlyph, CheckGlyph, delay, tone } from '../primitives';

/** The demo business's chat widget, playing its conversation once. */
export function ChatWidgetDemo({
  brand,
  messages,
}: {
  brand: DemoBrand;
  messages: DemoMessage[];
}) {
  const { t: tc } = useLocale('chatbots');

  return (
    <div className='overflow-hidden rounded-[22px] bg-white text-start shadow-[0_40px_90px_-30px_rgba(22,12,90,0.75)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
      <div className='flex items-center gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
        <span
          className={clsxm(
            'flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-semibold text-white',
            brand.bg,
          )}
        >
          {brand.initial}
        </span>
        <div className='min-w-0 flex-1'>
          <p className={clsxm('truncate text-[15px] font-semibold', tone.ink)}>
            {brand.name}
          </p>
          <p className={clsxm('flex items-center gap-1.5 text-xs', tone.quiet)}>
            <span className='h-1.5 w-1.5 flex-none rounded-full bg-emerald-500' />
            <span className='truncate'>
              {brand.kind} · {tc('Replies instantly')}
            </span>
          </p>
        </div>
      </div>

      <div className='space-y-3 px-5 py-5 text-[14px] leading-relaxed'>
        {messages.map((message, index) =>
          message.from === 'visitor' ? (
            <VisitorBubble key={index} at={message.at}>
              {message.text}
            </VisitorBubble>
          ) : (
            <AgentReply key={index} typingAt={message.typingAt} at={message.at}>
              {typeof message.content === 'string' ? (
                <AgentBubble brand={brand.bg}>{message.content}</AgentBubble>
              ) : (
                message.content
              )}
            </AgentReply>
          ),
        )}
      </div>

      <div className='px-4 pb-4'>
        <div className='flex items-center gap-2 rounded-full bg-[#f3f3f7] py-2.5 pe-2.5 ps-4 dark:bg-white/[0.05]'>
          <span className={clsxm('flex-1 truncate text-sm', tone.quiet)}>
            {tc('Ask anything…')}
          </span>
          <span
            className={clsxm(
              'flex h-7 w-7 items-center justify-center rounded-full text-white',
              brand.bg,
            )}
          >
            <ArrowGlyph className='h-3.5 w-3.5 -rotate-90 rtl:rotate-90' />
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The agent as a Slack app in a direct message, where employees ask it
 * questions. Slack's own light theme, so it reads as Slack at a glance.
 */
export function SlackDemo({
  brand,
  messages,
  person,
}: {
  brand: DemoBrand;
  messages: DemoMessage[];
  /** The employee asking. */
  person: { name: string; initials: string };
}) {
  const { t } = useLocale('bot-use-cases');
  // A minute between exchanges, like a real conversation.
  const times = ['9:41', '9:41', '9:42', '9:42', '9:43', '9:43'];

  return (
    <div className='overflow-hidden rounded-[18px] bg-white text-start shadow-[0_40px_90px_-30px_rgba(22,12,90,0.75)] ring-1 ring-black/5 dark:bg-[#1a1d21] dark:ring-white/10'>
      <div className='flex items-center gap-2.5 border-b border-black/[0.08] px-4 py-3 dark:border-white/[0.08]'>
        <FaSlack className='h-4 w-4 flex-none text-[#4A154B] dark:text-white' />
        <span
          className={clsxm(
            'flex h-6 w-6 flex-none items-center justify-center rounded-md text-[11px] font-bold text-white',
            brand.bg,
          )}
        >
          {brand.initial}
        </span>
        <span className={clsxm('truncate text-[15px] font-bold', tone.ink)}>
          {brand.name}
        </span>
        <span className='flex-none rounded bg-[#1d1c1d]/[0.08] px-1 py-px text-[10px] font-bold uppercase tracking-wide text-[#616061] dark:bg-white/10 dark:text-[#abacad]'>
          {t('App')}
        </span>
      </div>

      <div className='space-y-4 px-4 py-5 text-[14px] leading-relaxed'>
        {messages.map((message, index) => {
          const fromAgent = message.from === 'agent';
          const body = fromAgent ? message.content : message.text;
          return (
            <div key={index} className='grid'>
              {fromAgent && message.typingAt !== undefined && (
                <p
                  className={clsxm(
                    styles.typing,
                    'self-start text-xs italic [grid-area:1/1]',
                    tone.quiet,
                  )}
                  style={delay(message.typingAt, {
                    '--len': `${message.at - message.typingAt}ms`,
                  })}
                >
                  {t('{{name}} is typing…', { name: brand.name })}
                </p>
              )}
              <div
                className={clsxm(styles.bubble, 'flex gap-2.5 [grid-area:1/1]')}
                style={delay(message.at)}
              >
                <span
                  className={clsxm(
                    'flex h-9 w-9 flex-none items-center justify-center rounded-lg text-[13px] font-bold text-white',
                    fromAgent ? brand.bg : 'bg-[#7c6f64]',
                  )}
                >
                  {fromAgent ? brand.initial : person.initials}
                </span>
                <div className='min-w-0'>
                  <p className='flex items-baseline gap-2'>
                    <span className={clsxm('text-[14px] font-bold', tone.ink)}>
                      {fromAgent ? brand.name : person.name}
                    </span>
                    <span
                      className={clsxm('text-[11px] tabular-nums', tone.quiet)}
                    >
                      {times[index] ?? times[times.length - 1]}
                    </span>
                  </p>
                  <div className={clsxm('mt-0.5', tone.ink)}>{body}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className='px-4 pb-4'>
        <div
          className={clsxm(
            'rounded-lg px-3 py-2.5 text-sm ring-1 ring-black/15 dark:ring-white/20',
            tone.quiet,
          )}
        >
          {t('Message {{name}}', { name: brand.name })}
        </div>
      </div>
    </div>
  );
}

/** What the agent did out of sight while it answered. */
export function StepsCard({ steps }: { steps: DemoStep[] }) {
  const { t } = useLocale('bot-use-cases');

  return (
    <div className='rounded-2xl bg-[#0d0c1f] p-4 text-start text-white shadow-[0_30px_70px_-25px_rgba(8,6,40,0.8)] ring-1 ring-white/10'>
      <p className='flex items-center gap-2 text-xs font-medium text-white/60'>
        <LightningBoltIcon className='h-3.5 w-3.5' />
        {t('Behind the scenes')}
      </p>
      <ol className='mt-3 space-y-2.5'>
        {steps.map(({ icon: Icon, title, via, at }) => (
          <li
            key={title}
            className={clsxm(styles.enter, 'flex items-center gap-3')}
            style={delay(at)}
          >
            <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white/10'>
              <Icon className='h-3.5 w-3.5 text-white/80' />
            </span>
            <span className='min-w-0 flex-1'>
              <span className='block text-[13px] font-semibold leading-snug'>
                {title}
              </span>
              <span className='block truncate text-[11px] text-white/55'>
                {via}
              </span>
            </span>
            <CheckGlyph className='h-4 w-4 text-emerald-400' />
          </li>
        ))}
      </ol>
    </div>
  );
}
